import React, { useCallback } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import { EntityTypeSupportingPreview } from "@mapmycustomers/shared/types/entity";
import { Button, Popconfirm } from "@mapmycustomers/ui";

import { companyAnalytics, dealAnalytics, personAnalytics } from "@app/component/preview/analytics";
import { isCurrentUserReadOnly } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";
import { getEntityTypeDisplayName } from "@app/util/ui";

import styles from "./DeleteButton.module.scss";

const entityTypeToAnalyticsMap: Record<EntityTypeSupportingPreview, AnalyticsService> = {
  [EntityType.COMPANY]: companyAnalytics,
  [EntityType.DEAL]: dealAnalytics,
  [EntityType.PERSON]: personAnalytics,
};

interface Props {
  deleting?: boolean;
  entityType: EntityTypeSupportingPreview;
  isReadOnlyMember?: boolean;
  onDelete: () => void;
}

const DeleteButton: React.FC<Props> = ({ deleting, entityType, isReadOnlyMember, onDelete }) => {
  const intl = useIntl();
  const isDisabled = deleting || isReadOnlyMember;

  const handleDeleteAnalytics = useCallback(() => {
    onDelete();
    entityTypeToAnalyticsMap[entityType].clicked(["Delete Record"]);
  }, [entityType, onDelete]);

  return (
    <Popconfirm
      cancelText={intl.formatMessage({
        id: "preview.record.delete.confirmation.cancel",
        defaultMessage: "Cancel",
        description: "Cancel button in a Delete Record confirmation in the Record Edit pane",
      })}
      danger
      description={intl.formatMessage({
        id: "preview.record.delete.confirmation.description",
        defaultMessage: "You have the option to restore deleted records for up to 90 days.",
        description: "Delete popconfirm title in the Record Edit pane",
      })}
      okButtonProps={{ disabled: deleting, loading: deleting }}
      okText={intl.formatMessage({
        id: "preview.record.delete.confirmation.delete",
        defaultMessage: "Delete",
        description: "Delete button in a Delete Record confirmation in the Record Edit pane",
      })}
      onConfirm={handleDeleteAnalytics}
      title
      trigger={isDisabled ? [] : ["click"]}
    >
      <Button block className={styles.container} danger disabled={isDisabled} loading={deleting}>
        {intl.formatMessage(
          {
            id: "preview.record.delete",
            defaultMessage: "Delete {entityName}",
            description: "Delete button in the Record Edit pane",
          },
          { entityName: getEntityTypeDisplayName(intl, entityType, { lowercase: false }) }
        )}
      </Button>
    </Popconfirm>
  );
};

const mapStateToProps = (state: RootState) => ({
  isReadOnlyMember: isCurrentUserReadOnly(state),
});

export default connect(mapStateToProps)(DeleteButton);
